import type { FC } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import HourglassIcon from '@/assets/images/desk/hourglass.svg';
import ClockIcon from '@/assets/images/desk/clock.svg';
import { ProgressBars } from './ProgressBars';
import { LocalStorageKeys } from '@/constants/localStorage';
import { useTimePanelController } from './hooks/useTimePanelController';
import Tooltip from '@/components/Tooltip';

const TimeProgressPanel: FC = () => {
    const { t } = useTranslation('timePanel');

    const { containerRef, fontSize, isCountdown, toggleTimeMode, progress, lifeProgress } =
        useTimePanelController({
            storageKey: LocalStorageKeys.PROGRESS_PANEL_MODE,
        });

    const toggleLabel = isCountdown ? t('toggle.toNormal') : t('toggle.toCountdown');
    const iconAlt = isCountdown ? t('toggle.clockAlt') : t('toggle.hourglassAlt');
    const navigate = useNavigate();

    const handleAddLifeBar = useCallback(() => {
        navigate('/settings#birth-year');
    }, [navigate]);

    return (
        <div
            ref={containerRef}
            style={{ fontSize: `${fontSize}px` }}
            className="w-full h-full flex flex-col justify-center items-stretch relative px-3"
        >
            <Tooltip content={toggleLabel}>
                <button
                    className="absolute top-0 right-0 inline-flex h-8 w-8 items-center justify-center rounded-md border-0 bg-transparent text-gray-500 transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
                    onClick={toggleTimeMode}
                    aria-label={toggleLabel}
                    type="button"
                >
                    {isCountdown ? (
                        <img src={ClockIcon} alt={iconAlt} className="filter invert-[0.5]" />
                    ) : (
                        <img src={HourglassIcon} alt={iconAlt} className="filter invert-[0.5]" />
                    )}
                </button>
            </Tooltip>
            <div className="flex-1 flex items-center justify-center w-full">
                <ProgressBars
                    progress={progress}
                    isCountdown={isCountdown}
                    lifeProgress={lifeProgress}
                    onAddLifeBar={handleAddLifeBar}
                />
            </div>
        </div>
    );
};

export default TimeProgressPanel;
